import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Wallet,
  Users,
  Plus,
  Search,
  Clock,
  Euro,
  Loader2,
  CheckCircle2,
  CreditCard,
  Building2,
  Banknote,
  Gift,
  PenLine,
  RefreshCw
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { APP_VERSION, formatVersionDate } from '../../config/version';

const ClientsWalletsPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Credit dialog
  const [showCreditDialog, setShowCreditDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [creditForm, setCreditForm] = useState({
    hours: '',
    amount_ht: '',
    payment_source: 'wire_transfer',
    reference: '',
    notes: ''
  });
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const res = await api.get('/wallet/admin/clients-wallets');
      setClients(res.data || []);
    } catch (error) {
      console.error('Error fetching clients wallets:', error);
      toast.error('Erreur lors du chargement des wallets');
    } finally {
      setLoading(false);
    }
  };

  const openCreditDialog = (client) => {
    setSelectedClient(client);
    setCreditForm({
      hours: '',
      amount_ht: '',
      payment_source: 'wire_transfer',
      reference: '',
      notes: ''
    });
    setShowCreditDialog(true);
  };

  const handleCredit = async () => {
    if (!selectedClient) return;
    
    const hours = parseFloat(creditForm.hours);
    const amount = parseFloat(creditForm.amount_ht);
    
    if (!hours || hours <= 0) {
      toast.error('Veuillez indiquer un nombre d\'heures valide');
      return;
    }
    
    if (!amount || amount <= 0) {
      toast.error('Veuillez indiquer un montant valide');
      return;
    }
    
    setProcessing(true);
    try {
      const res = await api.post('/wallet/admin/manual-credit', {
        client_id: selectedClient.client_id,
        hours,
        amount_ht: amount,
        payment_source: creditForm.payment_source,
        reference: creditForm.reference || null,
        notes: creditForm.notes || null
      });
      
      toast.success(`${res.data.hours_credited}h créditées sur le wallet de ${res.data.client_name}`);
      setShowCreditDialog(false);
      setSelectedClient(null);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du crédit');
    } finally {
      setProcessing(false);
    }
  };

  const filteredClients = clients.filter(client => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      client.client_name?.toLowerCase().includes(search) ||
      client.client_email?.toLowerCase().includes(search)
    );
  });

  const totalBalance = clients.reduce((sum, c) => sum + (c.balance_hours || 0), 0);
  const totalReserved = clients.reduce((sum, c) => sum + (c.reserved_hours || 0), 0);

  const paymentSources = [
    { value: 'wire_transfer', label: 'Virement bancaire', icon: Building2 },
    { value: 'check', label: 'Chèque', icon: Banknote },
    { value: 'store_credit', label: 'Avoir', icon: Gift },
    { value: 'cash', label: 'Espèces', icon: Euro },
    { value: 'adjustment', label: 'Ajustement', icon: PenLine },
    { value: 'other', label: 'Autre', icon: CreditCard }
  ];

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 p-6 md:p-8",
        collapsed ? "md:ml-20" : "md:ml-64"
      )} style={{ paddingTop: "130px" }}>
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3" style={{ color: theme.colors?.primary }}>
              <Wallet className="w-8 h-8" />
              Wallets Clients
            </h1>
            <p className="text-[#64748b] mt-1">
              Gérez les wallets de vos clients et créditez manuellement
            </p>
          </div>
          <Button variant="outline" onClick={fetchClients} disabled={loading}>
            <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
            Actualiser
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Card className="card">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <Users className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="text-lg font-bold" style={{ color: theme.colors?.primary }}>{clients.length}</p>
                <p className="text-xs text-[#64748b]">Clients</p>
              </div>
            </CardContent>
          </Card>

          <Card className="card">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-100">
                <Clock className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <p className="text-lg font-bold text-emerald-600">{totalBalance.toFixed(1)}h</p>
                <p className="text-xs text-[#64748b]">Solde total</p>
              </div>
            </CardContent>
          </Card>

          <Card className="card">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100">
                <Clock className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <p className="text-lg font-bold text-amber-600">{totalReserved.toFixed(1)}h</p>
                <p className="text-xs text-[#64748b]">Réservé total</p>
              </div>
            </CardContent>
          </Card>

          <Card className="card">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100">
                <Wallet className="w-4 h-4 text-purple-600" />
              </div>
              <div>
                <p className="text-lg font-bold text-purple-600">{clients.filter(c => c.has_wallet).length}</p>
                <p className="text-xs text-[#64748b]">Wallets actifs</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="relative max-w-md mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8]" />
          <Input 
            placeholder="Rechercher un client..." 
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Clients List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: theme.colors?.primary }} />
          </div>
        ) : filteredClients.length === 0 ? (
          <Card className="card">
            <CardContent className="p-12 text-center text-[#64748b]">
              Aucun client trouvé
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">
            {filteredClients.map((client) => (
              <Card key={client.client_id} className="card hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Avatar className="w-12 h-12">
                        <AvatarImage src={client.avatar_url} />
                        <AvatarFallback className="bg-slate-200 text-slate-600">
                          {client.client_name?.charAt(0) || 'C'}
                        </AvatarFallback>
                      </Avatar>
                      
                      <div>
                        <button 
                          onClick={() => navigate(`/clients/${client.client_id}`)}
                          className="font-semibold hover:text-[#00d4aa] hover:underline transition-colors"
                          style={{ color: theme.colors?.primary }}
                        >
                          {client.client_name}
                        </button>
                        <p className="text-sm text-[#64748b]">{client.client_email}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-6">
                      {/* Balance */}
                      <div className="text-right">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-emerald-600" />
                          <span className="text-lg font-bold text-emerald-600">
                            {(client.balance_hours || 0).toFixed(1)}h
                          </span>
                        </div>
                        <p className="text-xs text-[#64748b]">Disponible</p>
                      </div>
                      
                      {/* Reserved */}
                      {client.reserved_hours > 0 && (
                        <div className="text-right">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-amber-600" />
                            <span className="text-lg font-bold text-amber-600">
                              {client.reserved_hours.toFixed(1)}h
                            </span>
                          </div>
                          <p className="text-xs text-[#64748b]">Réservé</p>
                        </div>
                      )}
                      
                      {/* Credit Button */}
                      <Button 
                        onClick={() => openCreditDialog(client)}
                        className="btn-primary"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Créditer
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Credit Dialog */}
        <Dialog open={showCreditDialog} onOpenChange={setShowCreditDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5 text-emerald-600" />
                Créditer le wallet
              </DialogTitle>
              <DialogDescription>
                {selectedClient && (
                  <button 
                    onClick={() => {
                      setShowCreditDialog(false);
                      navigate(`/clients/${selectedClient.client_id}`);
                    }}
                    className="font-medium text-slate-800 hover:text-[#00d4aa] hover:underline transition-colors"
                  >
                    {selectedClient.client_name}
                  </button>
                )}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Payment Source */}
              <div className="space-y-2">
                <Label>Origine du paiement *</Label>
                <Select 
                  value={creditForm.payment_source} 
                  onValueChange={(val) => setCreditForm(prev => ({ ...prev, payment_source: val }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentSources.map((source) => (
                      <SelectItem key={source.value} value={source.value}>
                        <div className="flex items-center gap-2">
                          <source.icon className="w-4 h-4" />
                          {source.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Hours & Amount */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Heures à créditer *</Label>
                  <Input 
                    type="text"
                    inputMode="decimal"
                    value={creditForm.hours}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9.,]/g, '').replace(',', '.');
                      setCreditForm(prev => ({ ...prev, hours: val }));
                    }}
                    placeholder="Ex: 10"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Montant HT (€) *</Label>
                  <Input 
                    type="text"
                    inputMode="decimal"
                    value={creditForm.amount_ht}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9.,]/g, '').replace(',', '.');
                      setCreditForm(prev => ({ ...prev, amount_ht: val }));
                    }}
                    placeholder="Ex: 900"
                  />
                </div>
              </div>

              {/* TTC Preview */}
              {creditForm.amount_ht && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span>Montant TTC (20%)</span>
                    <span className="font-bold" style={{ color: theme.colors?.primary }}>
                      {(parseFloat(creditForm.amount_ht || 0) * 1.2).toLocaleString('fr-FR')}€
                    </span>
                  </div>
                </div>
              )}

              {/* Reference */}
              <div className="space-y-2">
                <Label>Référence (optionnel)</Label>
                <Input 
                  value={creditForm.reference}
                  onChange={(e) => setCreditForm(prev => ({ ...prev, reference: e.target.value }))}
                  placeholder="Ex: Chèque n°123456, Virement du 26/01..."
                />
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label>Notes (optionnel)</Label>
                <Textarea 
                  value={creditForm.notes}
                  onChange={(e) => setCreditForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Notes internes..."
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreditDialog(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleCredit}
                disabled={processing}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {processing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                )}
                Créditer le wallet
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Footer with version */}
        <footer className="mt-8 pt-4 border-t border-slate-200 text-center">
          <p className="text-xs text-slate-400">
            Version {APP_VERSION.version} • Mise à jour le {formatVersionDate(APP_VERSION.buildDate)}
          </p>
        </footer>
      </main>
    </div>
  );
};

export default ClientsWalletsPage;
